import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiRefreshCw, FiUser, FiShield, FiLink, FiCheck, FiEdit, FiX } from 'react-icons/fi';
import AdminService from '../services/AdminService';

const Partners = () => {
  const [staff, setStaff] = useState([]);
  const [brands, setBrands] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'add' | 'edit' | 'link'
  const [selectedPartner, setSelectedPartner] = useState(null);

  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', role: 'Partner' });
  const [editForm, setEditForm] = useState({ id: '', name: '', email: '', phone: '', role: 'Partner', status: 'Active', brandId: '', instituteId: '' });
  const [linkForm, setLinkForm] = useState({ brandId: '', instituteId: '' });

  const extractArray = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.brands)) return res.brands;
    if (Array.isArray(res.institutes)) return res.institutes;
    return [];
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await AdminService.getAllStaff();
      setStaff(extractArray(res));

      try {
        const bRes = await AdminService.getBrands();
        setBrands(extractArray(bRes));
      } catch (e) {
        console.error('Error fetching admin brands:', e);
      }

      try {
        const iRes = await AdminService.getInstitutes();
        setInstitutes(extractArray(iRes));
      } catch (e) {
        console.error('Error fetching admin institutes:', e);
      }

    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const partners = staff.filter(s => s.role === 'Partner');
  const admins = staff.filter(s => s.role === 'Admin');

  const openAdd = () => {
    setForm({ name: '', email: '', password: '', phone: '', role: 'Partner' });
    setModal('add');
  };

  const openEditModal = (s) => {
    setSelectedPartner(s);
    setEditForm({
      id: s._id,
      name: getName(s.name),
      email: s.email || '',
      phone: s.phone || '',
      role: s.role || 'Partner',
      status: s.status || 'Active',
      brandId: s.brandId?._id || s.brandId || '',
      instituteId: s.instituteId?._id || s.instituteId || '',
    });
    setModal('edit');
  };

  const openLinkModal = (partner) => {
    setSelectedPartner(partner);
    setLinkForm({
      brandId: partner.brandId?._id || partner.brandId || '',
      instituteId: partner.instituteId?._id || partner.instituteId || '',
    });
    setModal('link');
  };

  const handleSaveAdd = async () => {
    try {
      await AdminService.addStaff({
        name: { first: form.name.split(' ')[0] || form.name, last: form.name.split(' ').slice(1).join(' ') || '' },
        email: form.email,
        password: form.password || '12345678',
        phone: form.phone,
        role: form.role,
      });
      setModal(null);
      loadData();
    } catch (err) { alert('Failed to add: ' + (err.response?.data?.message || err.message)); }
  };

  const handleSaveEdit = async () => {
    if (!editForm.id) return;
    try {
      // 1. Update basic staff details
      await AdminService.updateStaff(editForm.id, {
        name: { first: editForm.name.split(' ')[0] || editForm.name, last: editForm.name.split(' ').slice(1).join(' ') || '' },
        email: editForm.email,
        phone: editForm.phone,
        role: editForm.role,
        userstatus: editForm.status,
      });

      // 2. Update brand/institute links if partner
      if (editForm.role === 'Partner') {
        await AdminService.updatePartnerLinks(editForm.id, {
          brandId: editForm.brandId,
          instituteId: editForm.instituteId,
          status: editForm.status,
          role: editForm.role,
        });
      }

      alert('Partner details updated successfully!');
      setModal(null);
      loadData();
    } catch (err) {
      alert('Failed to update partner: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSaveLinks = async () => {
    if (!selectedPartner) return;
    try {
      await AdminService.updatePartnerLinks(selectedPartner._id, {
        brandId: linkForm.brandId,
        instituteId: linkForm.instituteId,
      });
      alert('Partner brand & institute link updated!');
      setModal(null);
      loadData();
    } catch (err) {
      alert('Failed to update links: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try { await AdminService.deleteStaff(id); loadData(); }
    catch (err) { alert('Failed to delete'); }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    try { await AdminService.updateStaffStatus(id, newStatus); loadData(); }
    catch (err) { alert('Failed to update status'); }
  };

  const getName = (name) => {
    if (typeof name === 'string') return name;
    if (typeof name === 'object') return `${name.first || ''} ${name.last || ''}`.trim();
    return '-';
  };

  const getBrandName = (brandId) => {
    if (!brandId) return null;
    const bId = typeof brandId === 'object' ? brandId._id : brandId;
    const match = brands.find(b => b._id === bId);
    return match ? match.name : (typeof brandId === 'object' ? brandId.name : 'Linked Brand');
  };

  const renderTable = (title, list, icon) => (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-extrabold text-slate-900">{title}</h3>
          <span className="text-xs font-bold text-slate-600 bg-slate-200 px-2.5 py-0.5 rounded-full ml-1">{list.length}</span>
        </div>
      </div>
      {list.length === 0 ? (
        <div className="text-center py-12"><p className="text-xs text-slate-500 font-medium">No {title.toLowerCase()} found</p></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100/70">
                <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Linked Brand / Institute</th>
                <th className="px-4 py-3 text-left text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((s) => {
                const linkedBrandName = getBrandName(s.brandId);

                return (
                  <tr key={s._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-purple-700 text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-sm">
                          {getName(s.name).charAt(0).toUpperCase()}
                        </div>
                        <span className="text-xs font-bold text-slate-900">{getName(s.name)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-xs font-medium text-slate-600">{s.email}</td>
                    <td className="px-4 py-3.5 text-xs font-medium text-slate-600">{s.phone || '-'}</td>
                    <td className="px-4 py-3.5 text-xs">
                      {s.role === 'Partner' ? (
                        <button
                          onClick={() => openLinkModal(s)}
                          className="px-3 py-1.5 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-900 font-bold text-[11px] flex items-center gap-1.5 border border-purple-200 transition shadow-sm"
                        >
                          <FiLink className="w-3.5 h-3.5 text-purple-700" />
                          <span>{linkedBrandName ? `Brand: ${linkedBrandName}` : 'Link Brand / Institute'}</span>
                        </button>
                      ) : (
                        <span className="text-slate-400 font-medium">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <button onClick={() => handleToggleStatus(s._id, s.status)} className={`px-2.5 py-1 rounded-lg text-[10px] font-extrabold cursor-pointer border ${s.status === 'Active' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-rose-50 text-rose-800 border-rose-200'}`}>
                        {s.status || 'Active'}
                      </button>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(s)}
                          className="p-2 rounded-lg text-slate-500 hover:text-purple-700 hover:bg-purple-50 transition"
                          title="Edit Partner / Staff"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(s._id)}
                          className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6 max-w-6xl text-left">
      {/* Top Header */}
      <div className="flex items-center justify-between bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Staff & Partners Management</h1>
          <p className="text-xs font-medium text-slate-500 mt-1">{staff.length} total active members</p>
        </div>
        <div className="flex gap-3">
          <button onClick={loadData} className="p-2.5 rounded-xl bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition shadow-sm" title="Refresh">
            <FiRefreshCw className="w-4 h-4" />
          </button>
          <button onClick={openAdd} className="px-5 py-2.5 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition flex items-center gap-2 shadow-sm">
            <FiPlus className="w-4 h-4" /> Add Staff / Partner
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 bg-white border border-slate-200 rounded-2xl shadow-sm">
          <div className="w-7 h-7 border-2 border-purple-600/30 border-t-purple-600 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          {renderTable('Admins', admins, <FiShield className="w-4 h-4 text-purple-700" />)}
          {renderTable('Partners', partners, <FiUser className="w-4 h-4 text-emerald-700" />)}
        </div>
      )}

      {/* 1. Add Staff Modal */}
      {modal === 'add' && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-md space-y-4 text-left shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="text-sm font-bold text-slate-900">Add New Staff / Partner</h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-4 h-4" /></button>
            </div>

            <div className="space-y-3">
              <Inp label="Full Name" value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))} placeholder="e.g. Vikram Sharma" />
              <Inp label="Email Address" value={form.email} onChange={v => setForm(p => ({ ...p, email: v }))} placeholder="partner@tapcoe.com" />
              <Inp label="Phone Number" value={form.phone} onChange={v => setForm(p => ({ ...p, phone: v }))} placeholder="10-digit mobile number" />
              <Inp label="Password" value={form.password} onChange={v => setForm(p => ({ ...p, password: v }))} placeholder="Default: 12345678" />
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Role</label>
                <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium">
                  <option value="Partner">Partner</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">Cancel</button>
              <button onClick={handleSaveAdd} className="px-5 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition">Save Member</button>
            </div>
          </div>
        </div>
      )}

      {/* 2. EDIT / UPDATE PARTNER MODAL */}
      {modal === 'edit' && selectedPartner && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-lg space-y-4 text-left shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2">
                <FiEdit className="text-purple-700 w-4 h-4" />
                <h3 className="text-sm font-bold text-slate-900">Edit Partner / Staff Profile</h3>
              </div>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-4 h-4" /></button>
            </div>

            <div className="space-y-4">
              <Inp label="Full Contact Name" value={editForm.name} onChange={v => setEditForm(p => ({ ...p, name: v }))} />
              <Inp label="Email Address" value={editForm.email} onChange={v => setEditForm(p => ({ ...p, email: v }))} />
              <Inp label="Phone Number" value={editForm.phone} onChange={v => setEditForm(p => ({ ...p, phone: v }))} />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Role</label>
                  <select value={editForm.role} onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium">
                    <option value="Partner">Partner</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Account Status</label>
                  <select value={editForm.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium">
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {editForm.role === 'Partner' && (
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wider">Link Organization Brand & Institute</h4>
                  
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Linked Brand Profile ({brands.length} available)</label>
                    <select
                      value={editForm.brandId}
                      onChange={e => setEditForm(p => ({ ...p, brandId: e.target.value }))}
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                    >
                      <option value="">-- No Linked Brand --</option>
                      {brands.map(b => (
                        <option key={b._id} value={b._id}>{b.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Linked Institute ({institutes.length} available)</label>
                    <select
                      value={editForm.instituteId}
                      onChange={e => setEditForm(p => ({ ...p, instituteId: e.target.value }))}
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                    >
                      <option value="">-- No Linked Institute --</option>
                      {institutes.map(inst => (
                        <option key={inst._id} value={inst._id}>{inst.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-3 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">Cancel</button>
              <button onClick={handleSaveEdit} className="px-5 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition flex items-center gap-1.5">
                <FiCheck className="w-4 h-4" /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. Link Partner with Brand & Institute Modal */}
      {modal === 'link' && selectedPartner && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-md space-y-4 text-left shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2">
                <FiLink className="text-purple-700 w-4 h-4" />
                <h3 className="text-sm font-bold text-slate-900">Link Partner with Brand / Institute</h3>
              </div>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-4 h-4" /></button>
            </div>

            <p className="text-xs text-slate-600 font-medium">
              Partner: <strong className="text-slate-900">{getName(selectedPartner.name)}</strong> ({selectedPartner.email})
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Select Linked Brand ({brands.length} available)</label>
                <select
                  value={linkForm.brandId}
                  onChange={e => setLinkForm(p => ({ ...p, brandId: e.target.value }))}
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                >
                  <option value="">-- Select Brand --</option>
                  {brands.map(b => (
                    <option key={b._id} value={b._id}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Select Linked Institute ({institutes.length} available)</label>
                <select
                  value={linkForm.instituteId}
                  onChange={e => setLinkForm(p => ({ ...p, instituteId: e.target.value }))}
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                >
                  <option value="">-- Select Institute --</option>
                  {institutes.map(inst => (
                    <option key={inst._id} value={inst._id}>{inst.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">Cancel</button>
              <button onClick={handleSaveLinks} className="px-5 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition flex items-center gap-1.5">
                <FiCheck className="w-4 h-4" /> Save Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Inp = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">{label}</label>
    <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium" />
  </div>
);

export default Partners;
